import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getSystemTableColumns = async (token, tableColumnId) => {
  const url = tableColumnId
    ? `admin/table_columns/${tableColumnId}/system_table_columns`
    : 'admin/system_table_columns';
  const data = await apiGet({ url, token });
  return data;
};

const getSystemTableColumn = async (token, id) => {
  const url = `admin/system_table_columns/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createSystemTableColumn = async (token, tableColumnId, params) => {
  const url = `admin/table_columns/${tableColumnId}/system_table_columns`;
  const data = await apiPost({ url, token, objectName: 'system_table_column', params });
  return data;
};

const updateSystemTableColumn = async (token, id, params) => {
  const url = `admin/system_table_columns/${id}`;
  const data = await apiPatch({ url, token, objectName: 'system_table_column', params });
  return data;
};

const deleteSystemTableColumn = async (token, id) => {
  const url = `admin/system_table_columns/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminSystemTableColumns({ tableColumnId, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystemTableColumns(token, tableColumnId);
  const queryKey = tableColumnId
    ? ['admin-system-table-columns', tableColumnId]
    : 'admin-system-table-columns';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminSystemTableColumn({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getSystemTableColumn(token, id);
  const queryKey = ['admin-system-table-column', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminSystemTableColumn({ tableColumnId, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createSystemTableColumn(token, tableColumnId, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-columns');
        queryClient.invalidateQueries(['admin-system-table-columns', tableColumnId]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminSystemTableColumn({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateSystemTableColumn(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-columns');
        queryClient.invalidateQueries(['admin-system-table-column', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminSystemTableColumn({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteSystemTableColumn(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-system-table-columns');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
